/**
 * „Tygodnie" — the twelve weeks of the 12-week read, newest first, grouped by month: the Ankiety
 * stream's card, rows and compare control (`SurveysTab.module.css`, `features/survey-report`'s
 * `RefusableIconButton`). Pressing a week shows it in the pane; in compare mode the rows fill the A
 * and B slots, a new pick replacing the older one.
 */

import type { Ref } from 'react';
import { useTranslation } from 'react-i18next';

import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import scrollbar from '~/shared/ui/scrollbar.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { RefusableIconButton } from '~/features/survey-report';

import type { NutritionWeekMonth } from '../model/nutritionWeeks';

import { CompareGlyph } from './NutritionGlyphs';
import styles from './NutritionTab.module.css';
import survey from './SurveysTab.module.css';

const KEY = 'web:clientDetail.nutrition';
const SKELETON_ROWS = 6;

const CHIP_CLASS = {
  good: styles.chipGood,
  bad: styles.chipBad,
  neutral: undefined,
} as const;

interface NutritionWeeksProps {
  readonly months: readonly NutritionWeekMonth[];
  readonly count: number;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly selected: number;
  readonly onSelect: (index: number) => void;
  readonly compare: readonly [number, number] | null;
  readonly onToggleCompare: () => void;
  /** The compare toggle stays mounted, so closing the comparison from the pane hands focus here. */
  readonly compareToggleRef?: Ref<HTMLButtonElement>;
  readonly onPick: (index: number) => void;
}

export function NutritionWeeks({
  months,
  count,
  gate,
  failed,
  selected,
  onSelect,
  compare,
  onToggleCompare,
  compareToggleRef,
  onPick,
}: NutritionWeeksProps) {
  const { t } = useTranslation();
  return (
    <section className={`${String(survey.streamCard)} ${String(styles.weeksCard)}`} aria-busy={gate.waiting}>
      <div className={survey.streamHead}>
        <h2 className={survey.streamHeading}>{t(`${KEY}.weeksHeading`)}</h2>
        {gate.waiting || failed ? null : <b className={survey.streamCount}>{String(count)}</b>}
        <RefusableIconButton
          ref={compareToggleRef}
          className={`${String(survey.streamAction)} ${compare === null ? String(styles.compareIdle) : String(styles.compareOn)}`}
          label={t(compare === null ? `${KEY}.compareOpen` : `${KEY}.compareClose`)}
          refusedReason={compare !== null || count > 1 ? null : t(`${KEY}.compareRefused`)}
          onPress={onToggleCompare}
        >
          <CompareGlyph />
        </RefusableIconButton>
      </div>
      <div className={`${String(scrollbar.scrollbar)} ${String(survey.streamScroller)} ${String(styles.weeksScroller)}`}>
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
            {gate.skeleton ? (
              <span className={survey.skelStream}>
                {Array.from({ length: SKELETON_ROWS }, (_, row) => (
                  <i key={row} className={`${String(skel.bar)} ${String(survey.skelStreamRow)}`} />
                ))}
              </span>
            ) : null}
          </>
        ) : failed ? (
          <p className={survey.streamNote}>{t(`${KEY}.failed`)}</p>
        ) : (
          <div className={gate.reveal}>
            {months.map((month) => (
              <div className={survey.streamMonth} key={month.key}>
                <em className={survey.streamEyebrow}>{month.label}</em>
                {month.rows.map((row) => {
                  const on =
                    compare === null ? selected === row.index : compare.includes(row.index);
                  const body = (
                    <>
                      <span className={survey.streamText}>
                        <span className={survey.streamTitle}>{row.title}</span>
                        <span className={survey.streamWhen}>{row.subtitle}</span>
                      </span>
                      <span
                        className={`${String(survey.statusChip)} ${String(CHIP_CLASS[row.chip.tone] ?? (on ? styles.chipOnRow : ''))}`}
                      >
                        {row.chip.text}
                      </span>
                    </>
                  );
                  if (compare !== null) {
                    const side =
                      compare[0] === row.index
                        ? t(`${KEY}.pickA`)
                        : compare[1] === row.index
                          ? t(`${KEY}.pickB`)
                          : null;
                    return (
                      <button
                        aria-pressed={side !== null}
                        className={`${String(survey.streamRow)} ${String(styles.weekRow)} ${side === null ? '' : String(survey.streamRowOn)}`}
                        key={row.key}
                        onClick={() => {
                          onPick(row.index);
                        }}
                        type="button"
                      >
                        <i
                          aria-hidden="true"
                          className={`${String(survey.streamBox)} ${String(styles.pickBox)} ${side === null ? '' : String(styles.pickBoxOn)}`}
                        >
                          {side}
                        </i>
                        {body}
                        {side === null ? null : <span className={hidden.srOnly}>, {side}</span>}
                      </button>
                    );
                  }
                  return (
                    <button
                      aria-current={selected === row.index ? 'true' : undefined}
                      className={`${String(survey.streamRow)} ${String(styles.weekRow)} ${selected === row.index ? String(survey.streamRowOn) : ''}`}
                      key={row.key}
                      onClick={() => {
                        onSelect(row.index);
                      }}
                      type="button"
                    >
                      {body}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
